import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { Task, TaskStatus, ReviewAttachment } from '@/types';
import { Upload, Trash2, Paperclip, FileText, Download, Eye } from 'lucide-react';
import { TaskChainAdjustModal } from './TaskChainAdjustModal';
import { getDownstreamTasks, getDaysDifference } from '@/utils/taskChain';
import { useProjectStore } from '@/stores';

interface TaskEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onSave: (task: Task) => void;
}

const statusOptions: { value: TaskStatus; label: string }[] = [
  { value: 'not-started', label: 'Not Started' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'delayed-completed', label: 'Delayed Completed' },
];

export function TaskEditModal({ isOpen, onClose, task, onSave }: TaskEditModalProps) {
  const { tasks, updateTask } = useProjectStore();
  const [formData, setFormData] = useState<Partial<Task>>({});
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showChainModal, setShowChainModal] = useState(false);
  const [originalEndDate, setOriginalEndDate] = useState('');
  const [affectedTasks, setAffectedTasks] = useState<Task[]>([]);
  const [daysDiff, setDaysDiff] = useState(0);
  const [showAcdInput, setShowAcdInput] = useState(false);
  const [acdError, setAcdError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (task) {
      setFormData({ 
        ...task, 
        description: '',
        reviewAttachments: task.reviewAttachments || []
      });
      setOriginalEndDate(task.end);
      if (task.status === 'delayed-completed') {
        setShowAcdInput(true);
      } else {
        setShowAcdInput(false);
      }
      setAcdError(false);
    }
  }, [task]);

  useEffect(() => {
    if (task && formData.end && showChainModal) {
      const diff = getDaysDifference(task.end, formData.end);
      setDaysDiff(diff);
      
      const downstream = getDownstreamTasks(task.id, tasks);
      setAffectedTasks(downstream);
    }
  }, [formData.end, task, tasks, showChainModal]);

  const handleSave = () => {
    if (formData && task) {
      if (formData.end !== originalEndDate && originalEndDate) {
        setShowChainModal(true);
        return;
      }
      if (formData.status === 'delayed-completed' && !formData.actualCompletionDate) {
        setAcdError(true);
        return;
      }
      setAcdError(false);
      onSave(formData as Task);
      onClose();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    
    const newAttachments: ReviewAttachment[] = [];
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${apiBaseUrl}/api/upload`, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error('Upload failed');
        }
        
        const result = await response.json();
        
        const attachment: ReviewAttachment = {
          id: `review-${Date.now()}-${i}`,
          fileName: file.name,
          fileUrl: result.url,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'Current User'
        };
        newAttachments.push(attachment);
      } catch (error) {
        console.error('File upload error:', error);
        alert(`文件 "${file.name}" 上传失败`);
      }
    }

    if (newAttachments.length > 0) {
      setFormData(prev => ({
        ...prev,
        reviewAttachments: [...(prev.reviewAttachments || []), ...newAttachments]
      }));
    }

    setIsUploading(false);
    
    // 重置file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    const attachment = (formData.reviewAttachments || []).find(a => a.id === attachmentId);
    if (attachment?.fileUrl) {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        await fetch(`${apiBaseUrl}/api/upload?url=${encodeURIComponent(attachment.fileUrl)}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.error('删除文件失败:', error);
      }
    }
    setFormData(prev => ({
      ...prev,
      reviewAttachments: (prev.reviewAttachments || []).filter(a => a.id !== attachmentId)
    }));
  };

  const calculateDuration = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return duration > 0 ? duration : 0;
  };

  const handleStartDateChange = (newStart: string) => {
    setFormData(prev => {
      const duration = calculateDuration(newStart, prev.end || newStart);
      return {
        ...prev,
        start: newStart,
        duration
      };
    });
  };

  const handleEndDateChange = (newEnd: string) => {
    setFormData(prev => {
      const duration = calculateDuration(prev.start || newEnd, newEnd);
      return {
        ...prev,
        end: newEnd,
        duration
      };
    });
  };

  const selectedStatus = statusOptions.find(s => s.value === formData.status);

  if (!task) return null;

  return (
    <React.Fragment>
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Task" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
          <Input
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <Input
              type="date"
              value={formData.start || ''}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <Input
              type="date"
              value={formData.end || ''}
              onChange={(e) => handleEndDateChange(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Days)</label>
          <Input
            type="number"
            value={formData.duration || 0}
            disabled
            className="bg-gray-100"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-calculated based on start and end dates</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Task Status</label>
          <div className="relative">
            {/* Dropdown Trigger */}
            <button
              onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}
              className={`
                w-full flex items-center justify-between px-4 py-3 border rounded-lg transition-all duration-300
                ${isStatusMenuOpen 
                  ? 'border-blue-600 bg-blue-600 text-white rounded-b-none' 
                  : 'border-gray-300 hover:border-blue-500 bg-white text-gray-800'}
              `}
            >
              <span className="font-medium">
                {selectedStatus?.label || 'Select Status'}
              </span>
              <svg 
                viewBox="0 0 360 360" 
                className={`w-4 h-4 transition-all duration-500 ${isStatusMenuOpen ? 'rotate-180 fill-white' : 'fill-current'}`}
              >
                <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            <div 
              className={`
                absolute top-full left-0 right-0 bg-white border border-blue-600 rounded-b-lg shadow-lg z-20 overflow-hidden
                transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
                ${isStatusMenuOpen 
                  ? 'opacity-100 visible translate-y-0 pointer-events-auto' 
                  : 'opacity-0 invisible -translate-y-3 pointer-events-none'}
              `}
            >
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFormData({ ...formData, status: option.value });
                    setIsStatusMenuOpen(false);
                    if (option.value === 'delayed-completed') {
                      setShowAcdInput(true);
                    } else {
                      setShowAcdInput(false);
                      setAcdError(false);
                    }
                  }}
                  className={`
                    w-full flex items-center px-4 py-3 text-left transition-all duration-300
                    ${formData.status === option.value
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-blue-600 hover:text-white'}
                  `}
                >
                  <span className="font-medium">{option.label}</span>
                </button>
              ))}
            </div>

            {/* Backdrop */}
            {isStatusMenuOpen && (
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsStatusMenuOpen(false)}
              />
            )}
          </div>
        </div>

        {showAcdInput && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <label className="block text-sm font-medium text-orange-700 mb-2">
              Actual Completion Date (ACD) - 实际完成日期
            </label>
            <Input
              type="date"
              value={formData.actualCompletionDate || ''}
              onChange={(e) => {
                setFormData({ ...formData, actualCompletionDate: e.target.value });
                setAcdError(false);
              }}
              className={acdError ? 'border-red-500' : ''}
            />
            {acdError && (
              <p className="text-xs text-red-500 mt-1">Please enter the actual completion date</p>
            )}
            <p className="text-xs text-orange-600 mt-2">
              Please confirm the actual completion date for this delayed-completed task
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter comments..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            rows={3}
          />
        </div>

        {/* 评审任务附件上传 */}
        {task.reviewTask && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                <Paperclip className="inline w-4 h-4 mr-1" />
                评审附件
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
                onChange={handleFileUpload}
                className="hidden"
                id="review-file-upload"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload className="w-4 h-4 mr-1" />
                {isUploading ? '上传中...' : '上传文件'}
              </Button>
            </div>

            {/* 附件列表 */}
            {(formData.reviewAttachments && formData.reviewAttachments.length > 0) ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.reviewAttachments.map((attachment) => (
                  <div 
                    key={attachment.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center overflow-hidden">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0 mr-2" />
                      <span className="text-sm text-gray-700 truncate">
                        {attachment.fileName}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400">
                        {new Date(attachment.uploadedAt).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => window.open(attachment.fileUrl, '_blank')}
                        className="text-blue-500 hover:text-blue-700"
                        title="查看"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={attachment.fileUrl}
                        download={attachment.fileName}
                        className="text-green-500 hover:text-green-700"
                        title="下载"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-700"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                暂无评审附件
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>

    <TaskChainAdjustModal
      isOpen={showChainModal}
      onClose={() => setShowChainModal(false)}
      onParentClose={onClose}
      task={task}
      newEndDate={formData.end || ''}
      latestFormData={{ start: formData.start || '', end: formData.end || '' }}
    />
    </React.Fragment>
  );
}
